import { ref, computed } from 'vue'
import cuentasBancariasServicio from '@/services/finanzas/cuentasBancariasServicio'
import Swal from 'sweetalert2'

/**
 * Composable para gestionar cuentas bancarias
 */
export function useCuentasBancarias() {
  const cuentas = ref([])
  const cuentaActual = ref(null)
  const cargando = ref(false)
  const error = ref(null)

  /**
   * Cuentas activas
   */
  const cuentasActivas = computed(() => {
    return cuentas.value.filter(c => c.activa)
  })

  /**
   * Saldo total de todas las cuentas activas
   */
  const saldoTotal = computed(() => {
    return cuentasActivas.value.reduce((total, cuenta) => {
      return total + parseFloat(cuenta.saldo_actual || 0)
    }, 0)
  })

  /**
   * Cargar todas las cuentas bancarias
   */
  const cargarCuentas = async () => {
    cargando.value = true
    error.value = null
    try {
      const respuesta = await cuentasBancariasServicio.listar()
      cuentas.value = respuesta
      return respuesta
    } catch (err) {
      error.value = err.message || 'Error al cargar cuentas bancarias'
      console.error(error.value, err)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.value
      })
      return []
    } finally {
      cargando.value = false
    }
  }

  /**
   * Obtener una cuenta específica
   */
  const obtenerCuenta = async (id) => {
    cargando.value = true
    error.value = null
    try {
      const respuesta = await cuentasBancariasServicio.obtener(id)
      cuentaActual.value = respuesta.cuenta
      return respuesta
    } catch (err) {
      error.value = err.mensaje || 'Error al obtener cuenta bancaria'
      console.error(error.value, err)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.value
      })
      return null
    } finally {
      cargando.value = false
    }
  }

  /**
   * Crear nueva cuenta bancaria
   */
  const crearCuenta = async (datos) => {
    cargando.value = true
    error.value = null
    try {
      const respuesta = await cuentasBancariasServicio.crear(datos)
      cuentas.value.unshift(respuesta)

      Swal.fire({
        icon: 'success',
        title: 'Cuenta creada',
        text: 'La cuenta bancaria se creó exitosamente',
        timer: 2000,
        showConfirmButton: false
      })

      return respuesta
    } catch (err) {
      error.value = err.mensaje || 'Error al crear cuenta bancaria'
      console.error(error.value, err)

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.value
      })

      throw err
    } finally {
      cargando.value = false
    }
  }

  /**
   * Actualizar cuenta bancaria
   */
  const actualizarCuenta = async (id, datos) => {
    cargando.value = true
    error.value = null
    try {
      const respuesta = await cuentasBancariasServicio.actualizar(id, datos)

      const index = cuentas.value.findIndex(c => c.id === id)
      if (index !== -1) {
        cuentas.value[index] = respuesta
      }

      Swal.fire({
        icon: 'success',
        title: 'Cuenta actualizada',
        text: 'La cuenta bancaria se actualizó exitosamente',
        timer: 2000,
        showConfirmButton: false
      })

      return respuesta
    } catch (err) {
      error.value = err.mensaje || 'Error al actualizar cuenta bancaria'
      console.error(error.value, err)

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.value
      })

      throw err
    } finally {
      cargando.value = false
    }
  }

  /**
   * Eliminar cuenta bancaria
   */
  const eliminarCuenta = async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar cuenta?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    })

    if (!result.isConfirmed) return false

    cargando.value = true
    error.value = null
    try {
      await cuentasBancariasServicio.eliminar(id)

      cuentas.value = cuentas.value.filter(c => c.id !== id)

      Swal.fire({
        icon: 'success',
        title: 'Cuenta eliminada',
        text: 'La cuenta bancaria se eliminó exitosamente',
        timer: 2000,
        showConfirmButton: false
      })

      return true
    } catch (err) {
      error.value = err.mensaje || 'Error al eliminar cuenta bancaria'
      console.error(error.value, err)

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.value
      })

      return false
    } finally {
      cargando.value = false
    }
  }

  /**
   * Conciliar cuenta bancaria
   */
  const conciliarCuenta = async (id, datos) => {
    cargando.value = true
    error.value = null
    try {
      const respuesta = await cuentasBancariasServicio.conciliar(id, datos)

      // Actualizar saldo en la lista
      const index = cuentas.value.findIndex(c => c.id === id)
      if (index !== -1) {
        cuentas.value[index].saldo_actual = datos.saldo_real
      }

      Swal.fire({
        icon: 'success',
        title: 'Conciliación exitosa',
        html: `
          <p>Saldo sistema: <strong>${respuesta.saldo_sistema}</strong></p>
          <p>Saldo real: <strong>${respuesta.saldo_real}</strong></p>
          <p>Diferencia: <strong>${respuesta.diferencia}</strong></p>
        `,
        confirmButtonText: 'Aceptar'
      })

      return respuesta
    } catch (err) {
      error.value = err.mensaje || 'Error al conciliar cuenta'
      console.error(error.value, err)

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.value
      })

      throw err
    } finally {
      cargando.value = false
    }
  }

  return {
    cuentas,
    cuentaActual,
    cargando,
    error,
    cuentasActivas,
    saldoTotal,
    cargarCuentas,
    obtenerCuenta,
    crearCuenta,
    actualizarCuenta,
    eliminarCuenta,
    conciliarCuenta
  }
}

export default useCuentasBancarias
